import { createApi } from '@reduxjs/toolkit/query/react';
import { ERROR_MESSAGES } from 'common/constants';
import { assert } from 'common/utils';
import { serializeDateFields } from 'common/utils/time';
import { PaginatedApiParamsV3, PaginatedApiPayloadV2 } from 'domain/Common';
import { CommunityId } from 'domain/CommunityId';
import { EventQuestionAssignmentType, EventStatus } from 'domain/event';
import flow from 'lodash/fp/flow';
import prop from 'lodash/fp/prop';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { mapTagFiltersToString } from 'store/utils';

import {
  AnswerStatistics,
  CreateInsightsReportParams,
  GetReportSourcesParams,
  InsightsReportData,
} from '../insightsReport';
import {
  mapAnswerStatisticsList,
  mapEventsListToEventReportSources,
  mapEventsReportDataToInsightsReportData,
  mapEventsReportListToInsightsReportList,
  mapCreatedEventsReport,
  mapExecutiveSummaryData,
  mapExistingEventsReport,
} from './mappers';
import {
  ConversationInsightsData,
  CreatedEventsReport,
  EventReportData,
  EventReportSource,
  ExistingEventsReport,
  type ExecutiveSummaryData,
  type FilterUsersApiParams,
  type FilterUsersData,
  type KeyMetricsData,
  SessionExplorationData,
} from './types';

import type { EventApiId } from 'store/apis/event/utils/eventApiId';

const noReportIdError = 'Report ID required';
const apiV2 = getApiBaseUrl('v2');
const apiV3 = getApiBaseUrl('v3');

export const eventsReportApi = createApi({
  reducerPath: 'eventsReportApi',
  tagTypes: ['REPORTS', 'SURVEY_REPORTS', 'REPORT_ANSWERS', 'REPORT_SOURCES'],
  baseQuery: axiosBaseQuery({ baseURL: '/' }),
  endpoints: (builder) => ({
    getEventsReportsList: builder.query<
      PaginatedApiPayloadV2<InsightsReportData>,
      PaginatedApiParamsV3 & { communityId: CommunityId | null }
    >({
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'REPORTS' as const, id })),
              { type: 'REPORTS' as const, id: 'LIST' },
            ]
          : ['REPORTS'],
      query: ({ communityId, ...params }) => {
        assert(communityId, ERROR_MESSAGES.noCommunityId);
        return {
          url: `${apiV2}/event/report/reports/${communityId}`,
          params,
        };
      },
      transformResponse: mapEventsReportListToInsightsReportList,
    }),
    getEventsReportById: builder.query<InsightsReportData, { reportId: string | undefined }>({
      providesTags: (_result, _error, { reportId }) => (reportId ? [{ type: 'REPORTS', id: reportId }] : []),
      query: ({ reportId }) => {
        assert(reportId, noReportIdError);
        return `${apiV2}/event/report/${reportId}`;
      },
      transformResponse: flow(serializeDateFields(), mapEventsReportDataToInsightsReportData),
    }),
    getIsAnyEventReport: builder.query<boolean, { communityId: CommunityId }>({
      query: ({ communityId }) => ({
        url: `${apiV2}/event/report/reports/${communityId}`,
        page: 1,
        size: 1,
      }),
      transformResponse: (data: PaginatedApiPayloadV2<EventReportData>) => data.totalCount > 0,
    }),
    getEventsReportSources: builder.query<PaginatedApiPayloadV2<EventReportSource>, GetReportSourcesParams>({
      providesTags: ['REPORT_SOURCES'],
      query: ({ page, pageSize, communityId, searchPhrase }) => ({
        url: `${apiV2}/event/report/events`,
        params: {
          page,
          size: pageSize,
          states: [EventStatus.Finished].join(','),
          communityId,
          searchText: searchPhrase,
        },
      }),
      transformResponse: mapEventsListToEventReportSources,
    }),
    getSimilarEventsReportSources: builder.mutation<PaginatedApiPayloadV2<EventReportSource>, { id: EventApiId }>({
      query: ({ id }) => ({
        url: `${apiV2}/event/${id}/similar-events`,
        params: { page: 1, size: 1_000_000 },
      }),
      transformResponse: mapEventsListToEventReportSources,
    }),
    getHasAssociatedEvents: builder.mutation<boolean, { id: EventApiId }>({
      query: ({ id }) => ({
        url: `${apiV2}/event/${id}/similar-events`,
        params: { page: 1, size: 1 },
      }),
      transformResponse: (payload: PaginatedApiPayloadV2<EventReportSource>) => !!payload.totalCount,
    }),
    getExistingEventsReport: builder.query<ExistingEventsReport, CreateInsightsReportParams>({
      providesTags: ['REPORTS'],
      query: ({ ids }) => ({
        url: `${apiV2}/event/report/existing`,
        params: { eventIds: ids.join(',') },
      }),
      transformResponse: mapExistingEventsReport,
    }),
    createEventsReport: builder.mutation<CreatedEventsReport, CreateInsightsReportParams>({
      query: ({ ids }) => ({
        url: `${apiV2}/event/report`,
        method: 'POST',
        data: { eventIds: ids },
      }),
      transformResponse: mapCreatedEventsReport,
      invalidatesTags: ['REPORTS'],
    }),
    retryEventsReportGeneration: builder.mutation<void, InsightsReportData>({
      invalidatesTags: (_result, _error, { id }) => [{ type: 'REPORTS', id }],
      query: ({ associatedItems, name }) => ({
        url: `${apiV2}/event/report`,
        method: 'POST',
        data: { eventIds: associatedItems.map(prop('id')), name },
      }),
    }),
    updateEventsReport: builder.mutation<void, Pick<InsightsReportData, 'id' | 'name'>>({
      query: ({ id, name }) => ({
        url: `${apiV3}/report/${id}`,
        method: 'PUT',
        data: { name },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'REPORTS', id }],
    }),
    deleteEventsReport: builder.mutation<void, Pick<InsightsReportData, 'id' | 'name' | 'status'>>({
      query: ({ id }) => ({
        url: `${apiV3}/report/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'REPORTS', id }],
    }),
    getEventsReportKeyMetrics: builder.query<KeyMetricsData, { reportId: string }>({
      providesTags: (_result, _error, args) => (args.reportId ? [{ type: 'REPORTS', id: args.reportId }] : []),
      query: ({ reportId }) => {
        assert(reportId, noReportIdError);
        return `${apiV2}/event/report/${reportId}/event-metrics`;
      },
    }),
    getEventsReportAnswers: builder.query<
      AnswerStatistics[],
      { reportId: string; assignmentType: EventQuestionAssignmentType; tagFilters?: Record<string, string[]> }
    >({
      providesTags: (result, _error, { reportId }) =>
        result?.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      serializeQueryArgs: ({ queryArgs, ...args }) => ({
        ...args,
        queryArgs: {
          ...queryArgs,
          tagFilters: mapTagFiltersToString(queryArgs.tagFilters),
        },
      }),
      query: ({ reportId, tagFilters, assignmentType }) => ({
        url: `${apiV2}/event/report/${reportId}/answers`,
        params: {
          eventQuestionAssignmentType: assignmentType,
          tagFilters: mapTagFiltersToString(tagFilters),
        },
      }),
      transformResponse: mapAnswerStatisticsList,
    }),
    exportEventsReport: builder.mutation<void, { reportId: string }>({
      query: ({ reportId }) => ({
        url: `${apiV3}/report/${reportId}/export`,
        method: 'POST',
      }),
    }),
    /**
     * V2
     */
    getSessionExplorationData: builder.query<SessionExplorationData, { reportId: string }>({
      providesTags: (result, _error, { reportId }) =>
        result?.topics.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      query: ({ reportId }) => ({
        url: `${apiV3}/report/${reportId}/sessionExplorationData`,
      }),
    }),
    getConversationInsights: builder.query<ConversationInsightsData, { reportId: string }>({
      providesTags: (result, _error, { reportId }) =>
        result?.topics.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      query: ({ reportId }) => ({
        url: `${apiV3}/report/${reportId}/conversationInsights`,
      }),
    }),
    getExecutiveSummary: builder.query<ExecutiveSummaryData, { reportId: string }>({
      providesTags: (result, _error, { reportId }) =>
        result?.isPublished !== undefined ? [{ type: 'REPORTS', id: reportId }] : [],
      query: ({ reportId }) => ({
        url: `${apiV3}/report/${reportId}/executiveSummary`,
      }),
      transformResponse: mapExecutiveSummaryData,
    }),
    updateExecutiveSummary: builder.mutation<void, { reportId: string; content: string; prevContent: string }>({
      query: ({ reportId, content }) => ({
        url: `${apiV3}/report/${reportId}/executiveSummary`,
        method: 'PUT',
        data: { content },
      }),
      invalidatesTags: (_result, _error, { reportId }) => [{ type: 'REPORTS', id: reportId }],
    }),
    filterUsers: builder.mutation<FilterUsersData, FilterUsersApiParams>({
      query: ({ reportId, filters }) => ({
        url: `${apiV3}/report/${reportId}/filterUsersByName`,
        method: 'POST',
        data: { filters },
      }),
    }),
  }),
});

export const {
  useGetEventsReportsListQuery,
  useRetryEventsReportGenerationMutation,
  useGetEventsReportByIdQuery,
  useGetEventsReportKeyMetricsQuery,
  useGetEventsReportAnswersQuery,
  useGetIsAnyEventReportQuery,
  useCreateEventsReportMutation,
  useLazyGetExistingEventsReportQuery,
  useUpdateEventsReportMutation,
  useDeleteEventsReportMutation,
  useLazyGetEventsReportSourcesQuery,
  useGetSimilarEventsReportSourcesMutation,
  useGetHasAssociatedEventsMutation,
  useExportEventsReportMutation,
  useGetSessionExplorationDataQuery,
  useGetConversationInsightsQuery,
  useGetExecutiveSummaryQuery,
  useUpdateExecutiveSummaryMutation,
  useFilterUsersMutation,
} = eventsReportApi;
